import { useMemo, useState } from 'react';
import {
    CheckIcon,
    CopyIcon,
    Link2Icon,
    MapPinIcon,
    NetworkIcon,
    Share2Icon,
    StarIcon,
    StoreIcon,
    UserPlusIcon,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { useFetch } from '@/hooks/useFecth';
import { useClub } from '../ClubContext';
import useClubErrors from '../hooks/useClubErrors';

const BENEFITS = [
    {
        icon: UserPlusIcon,
        title: 'Indique anunciantes',
        description: 'O dono do clube indica parceiros locais e convida lojas da região para a rede.',
    },
    {
        icon: StoreIcon,
        title: 'Cadastro por indicação',
        description: 'Esses anunciantes se cadastram pela indicação do clube, vinculados ao seu código.',
    },
    {
        icon: NetworkIcon,
        title: 'Enriqueça sua rede',
        description: 'Amplie a rede de lojas disponíveis para os assinantes do seu clube.',
    },
    {
        icon: StarIcon,
        title: 'Destaques locais',
        description: 'Crie destaques próprios na home com a sua rede local de anunciantes.',
    },
];

const STEPS = [
    {
        icon: Share2Icon,
        title: 'Compartilhe o link',
        description: 'Envie o link de indicação para o anunciante parceiro.',
    },
    {
        icon: Link2Icon,
        title: 'Código capturado',
        description: 'No cadastro, o código do clube é lido automaticamente pelo link.',
    },
    {
        icon: StoreIcon,
        title: 'Anunciante se cadastra',
        description: 'Ele preenche os dados no portal do anunciante e aguarda aprovação.',
    },
    {
        icon: MapPinIcon,
        title: 'Rede local pronta',
        description: 'Após aprovado, o parceiro entra na sua rede e pode aparecer nos destaques.',
    },
];

export default function ListAdvertisers() {
    const { club } = useClub();
    const { enqueueSnackbar } = useClubErrors();
    const [copied, setCopied] = useState(false);

    const { data: approvedData, isFetching: loadingApproved } = useFetch('club/advertisers', {
        params: {
            page: 1,
            per_page: 1,
            approved: true,
        },
    });

    const approvedCount = approvedData?.numRows ?? 0;
    const accessCode = club?.accessCode || '';

    const referralLink = useMemo(() => {
        if (!accessCode) return '';
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        return `${origin}/anunciante/registro-anunciante?code=${encodeURIComponent(accessCode)}`;
    }, [accessCode]);

    const handleCopy = async () => {
        if (!referralLink) {
            enqueueSnackbar('Código do clube ainda não disponível.', { variant: 'warning' });
            return;
        }
        try {
            await navigator.clipboard.writeText(referralLink);
            setCopied(true);
            enqueueSnackbar('Link de indicação copiado.');
            setTimeout(() => setCopied(false), 2000);
        } catch {
            enqueueSnackbar('Não foi possível copiar o link.', { variant: 'error' });
        }
    };

    const handleShare = async () => {
        if (!referralLink) return;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `Indicação — ${club?.name || 'Clube'}`,
                    text: 'Cadastre-se como anunciante pela indicação do nosso clube.',
                    url: referralLink,
                });
                return;
            } catch {
                // usuário cancelou ou share indisponível
            }
        }
        handleCopy();
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div className="flex flex-col gap-1">
                    <h1 className="text-2xl font-bold tracking-tight">Anunciantes</h1>
                    <p className="text-sm text-muted-foreground">
                        Indique parceiros locais e fortaleça a rede de lojas do seu clube.
                    </p>
                </div>
                <div className="flex items-center gap-3 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5 px-5 py-3">
                    <div className="flex size-10 items-center justify-center rounded-xl bg-primary/15 text-primary">
                        <StoreIcon className="size-5" />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-xs font-medium text-muted-foreground">Aprovados por indicação</span>
                        {loadingApproved && approvedData == null ? (
                            <Spinner className="size-5" />
                        ) : (
                            <span className="text-2xl font-semibold tracking-tight tabular-nums">{approvedCount}</span>
                        )}
                    </div>
                </div>
            </div>

            <Alert>
                <NetworkIcon />
                <AlertTitle>Rede própria por indicação</AlertTitle>
                <AlertDescription>
                    Em vez de cadastrar anunciantes manualmente, compartilhe o link com o código do seu clube. O
                    parceiro se cadastra sozinho e fica vinculado à sua rede.
                </AlertDescription>
            </Alert>

            <Card className="overflow-hidden">
                <CardHeader className="pb-3">
                    <CardTitle>Por que indicar</CardTitle>
                    <CardDescription>Benefícios de montar sua própria rede de anunciantes.</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="flex flex-col">
                        {BENEFITS.map((item, index) => {
                            const Icon = item.icon;
                            return (
                                <div key={item.title}>
                                    {index > 0 ? <Separator /> : null}
                                    <div className="flex gap-4 px-6 py-4 transition-colors hover:bg-muted/40">
                                        <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                                            <Icon className="size-5" />
                                        </div>
                                        <div className="flex min-w-0 flex-col gap-1">
                                            <p className="font-semibold leading-tight">{item.title}</p>
                                            <p className="text-sm text-muted-foreground">{item.description}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            <Card className="overflow-hidden border-primary/15 bg-gradient-to-b from-primary/8 via-muted/40 to-muted/20">
                <CardHeader>
                    <CardTitle>Como funciona</CardTitle>
                    <CardDescription>Passo a passo da indicação até a rede local.</CardDescription>
                </CardHeader>
                <CardContent>
                    <ol className="relative flex flex-col gap-0">
                        {STEPS.map((step, index) => {
                            const Icon = step.icon;
                            const isLast = index === STEPS.length - 1;
                            return (
                                <li key={step.title} className="relative flex gap-4 pb-6 last:pb-0">
                                    {!isLast ? (
                                        <span
                                            aria-hidden
                                            className="absolute top-11 left-5 h-[calc(100%-2.75rem)] w-px bg-primary/25"
                                        />
                                    ) : null}
                                    <div className="relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-background shadow-sm">
                                        <Icon className="size-4 text-primary" />
                                    </div>
                                    <div className="flex min-w-0 flex-1 flex-col gap-1.5 rounded-xl border border-border/60 bg-background/80 px-4 py-3 shadow-sm backdrop-blur-sm">
                                        <div className="flex items-center gap-2">
                                            <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                                                Passo {index + 1}
                                            </Badge>
                                            <p className="font-medium leading-tight">{step.title}</p>
                                        </div>
                                        <p className="text-sm text-muted-foreground">{step.description}</p>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </CardContent>
            </Card>

            <Card className="overflow-hidden">
                <CardHeader className="border-b bg-muted/30">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <CardTitle>Link de indicação</CardTitle>
                            <CardDescription className="mt-1">
                                Compartilhe este link. Ele inclui o código do clube para o cadastro do anunciante.
                            </CardDescription>
                        </div>
                        <Share2Icon className="size-5 shrink-0 text-muted-foreground" />
                    </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-4 p-6">
                    {accessCode ? (
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="text-muted-foreground">Código do clube</span>
                            <Badge variant="outline" className="font-mono text-sm">
                                {accessCode}
                            </Badge>
                        </div>
                    ) : (
                        <p className="text-sm text-muted-foreground">Código do clube indisponível no momento.</p>
                    )}

                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Input readOnly value={referralLink || '—'} className="font-mono text-xs sm:text-sm" />
                        <div className="flex shrink-0 gap-2">
                            <Button type="button" variant="outline" disabled={!referralLink} onClick={handleCopy}>
                                {copied ? <CheckIcon className="size-4" /> : <CopyIcon className="size-4" />}
                                {copied ? 'Copiado' : 'Copiar'}
                            </Button>
                            <Button type="button" disabled={!referralLink} onClick={handleShare}>
                                <Share2Icon className="size-4" />
                                Compartilhar
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
